import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { CapacitorUpdater, type BundleInfo, type DownloadFailedEvent } from '@capgo/capacitor-updater'
import { Sentry } from './sentry'
import { db } from './firebase'
import { terminate } from './firestore'
import { log } from './logger'
import { OtaManifestRejectedError, parseOtaManifest } from './otaManifest'
import { clearPrefetchFailures, shouldReportPrefetchFailure } from './otaPrefetchFailures'
import { WebViewRestart, canRestartWebView, isRestartInFlight, markRestartInFlight, saveRestorePath } from './webViewRestart'
import { backgroundSwapBlocker, transientStateReason } from './otaSafeToSwap'
import { MAX_APPLY_ATTEMPTS, decideUpdate, isNewer } from './otaPolicy'

const PREVIEW_CHANNEL_KEY = 'ota_preview_channel'
const APPLY_ATTEMPTS_KEY = 'ota_apply_attempts'
const LAST_BOOT_KEY = 'ota_last_boot'
const READY_MANIFEST_KEY = 'ota_ready_manifest'

// A channel id is interpolated straight into the manifest path, so it is held to
// a strict charset — no slashes, nothing that could climb out of ota/preview/.
const CHANNEL_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/

export interface OtaManifest {
  version: string
  url: string
  sha256: string
  nativeMinVersion: string
  releasedAt: string
  // Preview manifests carry provenance so the confirm screen can name what it
  // is about to install. Absent on production manifests.
  channel?: string
  branch?: string
  commit?: string
  subject?: string
}

export class OtaNativeTooOldError extends Error {
  required: string
  installed: string

  constructor(required: string, installed: string) {
    super(`This build needs app version ${required}; this device has ${installed}. Update from the store first.`)
    this.name = 'OtaNativeTooOldError'
    this.required = required
    this.installed = installed
  }
}

export function isValidChannelId(channel: string): boolean {
  return CHANNEL_PATTERN.test(channel)
}

export function getPreviewChannel(): string | null {
  const channel = localStorage.getItem(PREVIEW_CHANNEL_KEY)
  return channel && isValidChannelId(channel) ? channel : null
}

function readApplyAttempts(version: string): number {
  try {
    const raw = localStorage.getItem(APPLY_ATTEMPTS_KEY)
    if (!raw) return 0
    const parsed = JSON.parse(raw) as { version?: string; count?: number }
    return parsed.version === version ? (parsed.count ?? 0) : 0
  } catch {
    return 0
  }
}

function recordApplyAttempt(version: string): void {
  const count = readApplyAttempts(version) + 1
  localStorage.setItem(APPLY_ATTEMPTS_KEY, JSON.stringify({ version, count }))
}

function clearApplyAttempts(): void {
  localStorage.removeItem(APPLY_ATTEMPTS_KEY)
}

function storageBucket(): string {
  const bucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET
  if (!bucket) throw new Error('VITE_FIREBASE_STORAGE_BUCKET not set')
  return bucket
}

function manifestUrl(channel: string | null): string {
  const bucket = storageBucket()
  const path = channel ? `ota/preview/${channel}/version.json` : 'ota/version.json'
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media&t=${Date.now()}`
}

function isSatisfied(min: string, current: string): boolean {
  return !isNewer(min, current)
}

export async function fetchManifest(channel: string | null): Promise<OtaManifest | null> {
  const url = manifestUrl(channel)
  log('ota', 'fetching manifest', { url, channel })
  const response = await fetch(url, { cache: 'no-store' })
  log('ota', 'manifest response', { status: response.status, ok: response.ok })
  if (!response.ok) {
    if (response.status === 404) return null
    // The manifest is world-readable, so a 403 means something between the
    // device and Storage refused it (GlitchTip 8722838) — record who answered.
    const body = await response.text().catch(() => '')
    log('ota', 'manifest error response', {
      status: response.status,
      contentType: response.headers.get('content-type'),
      body: body.slice(0, 300),
    })
    throw new Error(`OTA manifest fetch failed: ${response.status}`)
  }
  const json: unknown = await response.json()
  try {
    return parseOtaManifest(json, { bucket: storageBucket(), channel })
  } catch (err) {
    if (err instanceof OtaManifestRejectedError) {
      const m = json as { version?: unknown; url?: unknown } | null
      log('ota', 'manifest rejected', {
        reason: err.reason,
        channel,
        version: typeof m?.version === 'string' ? m.version : undefined,
        url: typeof m?.url === 'string' ? m.url : undefined,
      })
    }
    throw err
  }
}

const MANIFEST_RETRY_DELAY_MS = 1500

// One device failed the manifest fetch for a few hundred ms while its other
// requests worked (#607); a systemic failure (CORS, ATS) still fails both tries.
async function fetchManifestWithRetry(channel: string | null): Promise<OtaManifest | null> {
  try {
    return await fetchManifest(channel)
  } catch (err) {
    if (!isFetchNetworkError(err)) throw err
    log('ota', 'manifest fetch failed, retrying', { error: (err as Error).message, delayMs: MANIFEST_RETRY_DELAY_MS })
    await new Promise(r => setTimeout(r, MANIFEST_RETRY_DELAY_MS))
    return fetchManifest(channel)
  }
}

// Android's download() rejects with only "Failed to download from: <url>"; the
// worker's actual error reaches JS solely on this event, untyped by capgo.
let nativeDownloadError: string | null = null
let watchingNativeDownloadErrors = false

function watchNativeDownloadErrors(): void {
  nativeDownloadError = null
  if (watchingNativeDownloadErrors) return
  watchingNativeDownloadErrors = true
  void CapacitorUpdater.addListener('downloadFailed', (event: DownloadFailedEvent & { error?: string }) => {
    if (event.error) nativeDownloadError = event.error
  }).catch(err => log('ota', 'download failure listener failed', { error: String(err) }))
}

function downloadBundle(manifest: OtaManifest): Promise<BundleInfo> {
  log('ota', 'download started', { version: manifest.version })
  watchNativeDownloadErrors()
  return CapacitorUpdater.download({
    url: manifest.url,
    version: manifest.version,
    checksum: manifest.sha256,
  })
}

// A bundle downloaded ahead of time but never queued with next() — capgo would
// install a queued bundle on the next backgrounding, via the in-process reload
// that trips the iOS IndexedDB hang. Left unqueued, it sits on disk until
// applyReadyBundleInBackground() swaps it in.
async function findPrefetched(version: string): Promise<BundleInfo | undefined> {
  const { bundles } = await CapacitorUpdater.list().catch(() => ({ bundles: [] as BundleInfo[] }))
  return bundles.find(b => b.version === version && (b.status === 'pending' || b.status === 'success'))
}

// The newest manifest whose bundle is on disk, waiting for the app to go to the
// background. Persisted so a cold start can swap it in before the first render
// when the app was killed without a real backgrounding (iOS's app switcher only
// resigns active).
let readyManifest: OtaManifest | null = null

function setReadyManifest(manifest: OtaManifest | null): void {
  readyManifest = manifest
  try {
    if (manifest) localStorage.setItem(READY_MANIFEST_KEY, JSON.stringify(manifest))
    else localStorage.removeItem(READY_MANIFEST_KEY)
  } catch {
    // The in-memory copy still serves the background swap.
  }
}

function storedReadyManifest(): OtaManifest | null {
  try {
    const raw = localStorage.getItem(READY_MANIFEST_KEY)
    return raw ? (JSON.parse(raw) as OtaManifest) : null
  } catch {
    return null
  }
}

let prefetchInFlight: Promise<void> | null = null

// Downloads an update in the foreground so the next backgrounding can swap it
// in without a download. Also drops any older prefetch the new one supersedes,
// so they don't pile up on disk. A newer manifest arriving mid-download is
// picked up by the next check.
function prefetch(manifest: OtaManifest): void {
  if (prefetchInFlight) return
  // A launch apply outlives its check's 10s timeout, so a resume can arrive
  // mid-download; a second download of the same version fails once the apply
  // switches bundles.
  if (applyInFlight?.version === manifest.version) return
  prefetchInFlight = (async () => {
    const native = (await App.getInfo()).version
    if (!isSatisfied(manifest.nativeMinVersion, native)) return
    const onDisk = await findPrefetched(manifest.version)
    if (onDisk) {
      clearPrefetchFailures()
      setReadyManifest(manifest)
      log('ota', 'bundle ready for background swap', { version: manifest.version, id: onDisk.id })
      return
    }

    const downloaded = await downloadBundle(manifest)
    clearPrefetchFailures()
    setReadyManifest(manifest)
    log('ota', 'prefetched bundle for background swap', { version: manifest.version, id: downloaded.id })

    const [{ bundles }, current, next] = await Promise.all([
      CapacitorUpdater.list(),
      CapacitorUpdater.current(),
      CapacitorUpdater.getNextBundle().catch(() => null),
    ])
    const keep = new Set([downloaded.id, current.bundle.id, next?.id, 'builtin'])
    // Only 'pending' bundles are unused downloads; the 'success' ones include
    // capgo's rollback fallback.
    const stale = bundles.filter(b => b.status === 'pending' && !keep.has(b.id))
    for (const bundle of stale) {
      await CapacitorUpdater.delete({ id: bundle.id })
        .then(() => log('ota', 'deleted superseded prefetch', { version: bundle.version, id: bundle.id }))
        .catch(err => log('ota', 'delete superseded prefetch failed', { id: bundle.id, error: String(err) }))
    }
  })()
    .catch(async err => {
      // capgo's "Failed to download from" hides why; an existing entry for the
      // same version in another state, or an apply that started meanwhile, would
      // explain a download that fails within milliseconds (GlitchTip 8691204).
      const { bundles } = await CapacitorUpdater.list().catch(() => ({ bundles: [] as BundleInfo[] }))
      const diagnostics = {
        version: manifest.version,
        error: String(err),
        nativeError: nativeDownloadError,
        sameVersionBundles:bundles.filter(b => b.version === manifest.version).map(b => `${b.id}:${b.status}`),
        bundleCount: bundles.length,
        applyInFlightVersion: applyInFlight?.version ?? null,
      }
      log('ota', 'prefetch failed', diagnostics)
      // The next resume retries, so a flaky connection isn't worth an issue.
      if (isOfflineDownloadError(err) || !shouldReportPrefetchFailure()) return
      // The message embeds the bundle URL, which would open a new issue per release.
      Sentry.captureException(err, { fingerprint: ['ota-prefetch-failed'], extra: diagnostics })
    })
    .finally(() => { prefetchInFlight = null })
}

const OFFLINE_DOWNLOAD = /Timed out|appears to be offline|network connection was lost|Could not connect to the server/i

export function isOfflineDownloadError(err: unknown): boolean {
  return OFFLINE_DOWNLOAD.test(String(err))
}

// Chrome/Android says "Failed to fetch", WebKit "Load failed" (sometimes with the
// host appended) — the same network-level failure, no HTTP response.
const FETCH_NETWORK_ERROR = /^(Failed to fetch|Load failed)\b/

export function isFetchNetworkError(err: unknown): boolean {
  return err instanceof TypeError && FETCH_NETWORK_ERROR.test(err.message)
}

// Downloads and activates a bundle unconditionally. The version gate lives in
// checkForUpdate — a preview swap deliberately ignores version ordering, since
// a preview bundle and production have no meaningful order between them and
// gating on it would strand the device on whichever happened to be higher.
let applyInFlight: { version: string; promise: Promise<{ applied: true; version: string }> } | null = null

// The check that starts an apply gives up waiting after 10s while the download
// carries on, so a preview poll or a Refresh tap can arrive mid-apply — join it
// instead of downloading and switching twice. Only the same version joins: an
// exit to production mid-download must not land on the preview bundle.
function applyManifest(manifest: OtaManifest): Promise<{ applied: true; version: string }> {
  if (applyInFlight?.version === manifest.version) return applyInFlight.promise
  const promise = applyManifestOnce(manifest).finally(() => {
    if (applyInFlight?.promise === promise) applyInFlight = null
  })
  applyInFlight = { version: manifest.version, promise }
  return promise
}

async function applyManifestOnce(manifest: OtaManifest): Promise<{ applied: true; version: string }> {
  setReadyManifest(null)
  const native = (await App.getInfo()).version
  if (!isSatisfied(manifest.nativeMinVersion, native)) {
    throw new OtaNativeTooOldError(manifest.nativeMinVersion, native)
  }

  const prefetched = await findPrefetched(manifest.version)
  if (prefetched) log('ota', 'using prefetched bundle', { version: manifest.version, id: prefetched.id })
  const bundle = prefetched ?? await downloadBundle(manifest)

  // Preserve the current route so the user lands back on the same page after reload
  const currentPath = saveRestorePath()

  // A version already attempted and still not running means the native restart
  // didn't activate it (an old shell, or iOS killing the app mid-swap) — take
  // the in-process fallback instead, so a broken activation can't restart-loop.
  const canRestart = canRestartWebView() && readApplyAttempts(manifest.version) === 0

  try {
    if (canRestart) {
      try {
        log('ota', 'applying bundle via native restart', { version: manifest.version, restorePath: currentPath })
        markRestartInFlight('ota_restart')
        // The old WKWebView outlives the swap by the cross-dissolve, and its
        // Firestore holds the same IndexedDB the new one boots from (no lease —
        // forceOwnership). Left running, it acks and deletes a write the new
        // instance already re-sent, and that instance's own ack then trips
        // removeMutationBatch's hard assert, poisoning every later call for
        // the session (#406). Shut it down first; a pending batch stays in
        // IndexedDB and the new instance resends it.
        await terminateWithTimeout()
        // Counted only once the swap itself starts, so iOS suspending the app
        // earlier never counts as a failed activation. Written before the
        // switch so it survives the restart.
        recordApplyAttempt(manifest.version)
        // Queued for as short a time as possible: capgo's own background
        // handler installs a queued bundle with the in-process reload that
        // trips the WebKit IDB hang. restart() activates it in a fresh
        // WKWebView and clears the queue.
        await CapacitorUpdater.next({ id: bundle.id })
        await WebViewRestart.restart()
        return { applied: true, version: manifest.version }
      } catch (err) {
        log('ota', 'native restart failed — falling back to in-process reload', { error: String(err) })
      }
    }

    // Signal the reload to use memoryLocalCache (see firebase.ts isOtaBoot)
    localStorage.setItem('ota_pending_reload', '1')
    markRestartInFlight('ota_restart')
    if (!canRestart) recordApplyAttempt(manifest.version)
    log('ota', 'applying bundle immediately', { version: manifest.version, restorePath: currentPath })
    await CapacitorUpdater.set({ id: bundle.id })
    // set() triggers an immediate reload — code below won't execute
    return { applied: true, version: manifest.version }
  } catch (err) {
    // Firestore may already be terminated, and isRestartInFlight() mutes error
    // UI and recovery for the rest of the session — never leave the page
    // running like that.
    log('ota', 'apply failed mid-swap — reloading', { error: String(err) })
    Sentry.captureException(err, { fingerprint: ['ota-apply-failed'] })
    window.location.reload()
    throw err
  }
}

const TERMINATE_TIMEOUT_MS = 1500

// iOS gives a backgrounded app only a few seconds; a hung terminate must not
// eat them all.
async function terminateWithTimeout(): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timedOut = new Promise<true>(resolve => { timer = setTimeout(() => resolve(true), TERMINATE_TIMEOUT_MS) })
  const result = await Promise.race([
    terminate(db).catch(err => log('ota', 'terminate before restart failed', { error: String(err) })),
    timedOut,
  ])
  clearTimeout(timer)
  if (result === true) log('ota', 'terminate before restart timed out', { ms: TERMINATE_TIMEOUT_MS })
}

// What a check does with a newer manifest is decideUpdate's call (otaPolicy.ts);
// a prefetched bundle is swapped in by applyReadyBundleInBackground. While a
// preview is pinned, useOTACheck also polls the channel in the foreground.
export type OtaCheckTrigger = 'launch' | 'resume' | 'poll'

export async function checkForUpdate(
  trigger: OtaCheckTrigger = 'launch',
): Promise<{ applied: boolean; version?: string }> {
  if (!Capacitor.isNativePlatform()) return { applied: false }

  // While pinned to a preview, track that channel rather than production — the
  // pin is only cleared by the user, through the header's exit affordance.
  const channel = getPreviewChannel()
  log('ota', 'check started', { platform: Capacitor.getPlatform(), channel, trigger })

  const manifest = await fetchManifestWithRetry(channel)
  if (!manifest) {
    log('ota', 'no manifest', { channel })
    return { applied: false }
  }
  log('ota', 'manifest parsed', { version: manifest.version, nativeMinVersion: manifest.nativeMinVersion })

  const current = await CapacitorUpdater.current()
  const installed = current.bundle.version
  log('ota', 'current bundle', { id: current.bundle.id, version: installed, status: current.bundle.status })
  log('ota', 'manifest fetched', { remoteVersion: manifest.version, installedVersion: installed, isNewer: isNewer(manifest.version, installed) })

  const attempts = readApplyAttempts(manifest.version)
  const decision = decideUpdate({
    remote: manifest.version,
    installed,
    preview: channel !== null,
    attempts,
    transientState: transientStateReason(),
  })

  if (decision.action === 'up_to_date') {
    log('ota', 'already up to date')
    // Whatever we were tracking either activated or was superseded.
    clearApplyAttempts()
    clearPrefetchFailures()
    setReadyManifest(null)
    return { applied: false }
  }

  if (decision.action === 'capped') {
    const detail = `OTA bundle ${manifest.version} was applied ${attempts} times without ever becoming active (device is still on ${installed}).`
    if (channel) {
      // Stay on production rather than chasing a channel this device can't run.
      localStorage.removeItem(PREVIEW_CHANNEL_KEY)
      clearApplyAttempts()
      log('ota', 'preview channel unpinned — bundle never activated', { channel, version: manifest.version, attempts })
      Sentry.captureMessage(`${detail} Unpinned preview channel "${channel}".`, 'warning')
    } else {
      // Keep the counter so this version stays skipped until a newer one ships.
      log('ota', 'skip — bundle never activated', { version: manifest.version, attempts })
      Sentry.captureMessage(detail, { level: 'warning', fingerprint: ['ota-never-activated'] })
    }
    return { applied: false }
  }

  if (decision.action === 'prefetch') {
    log('ota', 'prefetching for background swap', { trigger, remoteVersion: manifest.version, installedVersion: installed, reason: decision.reason })
    prefetch(manifest)
    return { applied: false }
  }

  try {
    return await applyManifest(manifest)
  } catch (err) {
    if (err instanceof OtaNativeTooOldError) {
      log('ota', 'skip — native too old', { required: err.required, installed: err.installed })
      Sentry.captureMessage(
        `OTA bundle ${manifest.version} requires native ${err.required}, but installed native is ${err.installed}. Skipping.`,
        'info',
      )
      return { applied: false }
    }
    throw err
  }
}

// Whether a prefetched bundle can be swapped in without a download: still
// ahead of the running bundle, for the channel this device tracks now, not
// given up on, and still on disk (capgo deletes a bundle it rolled back).
async function isSwappable(manifest: OtaManifest): Promise<boolean> {
  const channel = getPreviewChannel()
  if ((manifest.channel ?? null) !== channel) return false
  const installed = (await CapacitorUpdater.current()).bundle.version
  const ahead = channel ? isPreviewRepublish(manifest, installed) : isNewer(manifest.version, installed)
  if (!ahead || readApplyAttempts(manifest.version) >= MAX_APPLY_ATTEMPTS) return false
  if (!(await findPrefetched(manifest.version))) {
    log('ota', 'prefetched bundle no longer on disk', { version: manifest.version })
    return false
  }
  return true
}

// Called when the app goes to the background: swaps in the bundle prefetch()
// left on disk, so the user comes back to the new version instead of watching
// the app restart. Never downloads — a backgrounded app may be suspended any
// moment — and never throws.
export async function applyReadyBundleInBackground(backgroundedAt = Date.now()): Promise<void> {
  // An old WKWebView left running after a restart (shells before 2104c9e2)
  // still hears the lifecycle events; it must never swap.
  if (!Capacitor.isNativePlatform() || isRestartInFlight() || applyInFlight) return
  const manifest = readyManifest
  if (!manifest) return
  const blocker = backgroundSwapBlocker()
  if (blocker) {
    log('ota', 'background swap deferred', { version: manifest.version, reason: blocker })
    return
  }
  try {
    if (!(await isSwappable(manifest))) {
      setReadyManifest(null)
      return
    }
    log('ota', 'applying bundle in background', { version: manifest.version, msSinceBackground: Date.now() - backgroundedAt })
    await applyManifest(manifest)
  } catch (err) {
    log('ota', 'background swap failed', { version: manifest.version, error: String(err) })
    if (!(err instanceof OtaNativeTooOldError) && !isOfflineDownloadError(err)) Sentry.captureException(err)
  }
}

// Called before the first render: a bundle downloaded last session that never
// got its background swap (the app was killed from the switcher) goes in now,
// while the screen is still the splash background, instead of waiting a whole
// session. Resolves true when a swap started — the caller must not render,
// and gets onFailed if the swap dies before the restart.
export async function applyReadyBundleAtLaunch(onFailed: () => void): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false
  const manifest = storedReadyManifest()
  if (!manifest) return false
  try {
    if (!(await isSwappable(manifest))) {
      setReadyManifest(null)
      return false
    }
    log('ota', 'applying bundle before first render', { version: manifest.version })
    void applyManifest(manifest).catch(err => {
      log('ota', 'launch swap failed', { version: manifest.version, error: String(err) })
      Sentry.captureException(err)
      onFailed()
    })
    return true
  } catch (err) {
    log('ota', 'launch swap check failed', { error: String(err) })
    return false
  }
}

// Swaps the app onto a preview channel's bundle and pins it, so subsequent
// launches keep tracking that channel instead of production.
export async function loadPreviewChannel(channel: string): Promise<{ version: string }> {
  if (!Capacitor.isNativePlatform()) {
    throw new Error('Preview builds can only be loaded in the native app.')
  }
  if (!isValidChannelId(channel)) {
    throw new Error(`Invalid preview channel: ${channel}`)
  }

  const manifest = await fetchManifest(channel)
  if (!manifest) {
    throw new Error(`No preview published for "${channel}". It may have expired.`)
  }

  log('ota', 'loading preview channel', { channel, version: manifest.version })
  localStorage.setItem(PREVIEW_CHANNEL_KEY, channel)
  try {
    const result = await applyManifest(manifest)
    return { version: result.version }
  } catch (err) {
    // Never leave the pin set for a bundle that failed to install — the next
    // launch would keep checking a channel the device can't run.
    localStorage.removeItem(PREVIEW_CHANNEL_KEY)
    throw err
  }
}

// Any version other than the running one counts as newer: a channel's manifest
// always points at its latest publish, even if the branch's package.json
// version moved backwards in between (which would fool isNewer).
function isPreviewRepublish(manifest: OtaManifest, installed: string | null): boolean {
  return manifest.version !== installed
}

export async function getRunningBundleVersion(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null
  const current = await CapacitorUpdater.current()
  return current.bundle.version
}

// Re-checks the pinned preview channel right now and swaps onto its bundle if
// it was republished — the on-demand counterpart of useOTACheck's poll, which
// also reports back when there's nothing new.
export async function refreshPreviewChannel(): Promise<{ updated: boolean; version: string }> {
  const channel = getPreviewChannel()
  if (!Capacitor.isNativePlatform() || !channel) {
    throw new Error('No preview build is loaded.')
  }

  const [manifest, installed] = await Promise.all([fetchManifest(channel), getRunningBundleVersion()])
  if (!manifest) {
    throw new Error(`No preview published for "${channel}". It may have expired.`)
  }
  if (!isPreviewRepublish(manifest, installed)) {
    log('ota', 'preview already up to date', { channel, version: installed })
    return { updated: false, version: manifest.version }
  }

  log('ota', 'refreshing preview channel', { channel, from: installed, to: manifest.version })
  // Restarts into the new bundle; nothing after this resolves.
  const result = await applyManifest(manifest)
  return { updated: true, version: result.version }
}

// Returns to the production bundle. Always an explicit user action; nothing
// auto-reverts, so a preview survives relaunches until it is exited here.
export async function exitPreview(): Promise<{ version: string }> {
  localStorage.removeItem(PREVIEW_CHANNEL_KEY)

  const manifest = await fetchManifest(null)
  if (!manifest) {
    throw new Error('No production bundle published to return to.')
  }

  log('ota', 'exiting preview', { version: manifest.version })
  const result = await applyManifest(manifest)
  return { version: result.version }
}

// Marks the running bundle stable. Run once per boot, before any check that
// might apply a bundle, since it also clears capgo's next() slot.
export async function notifyAppReady(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  const [current, next] = await Promise.all([
    CapacitorUpdater.current(),
    CapacitorUpdater.getNextBundle().catch(() => null),
  ])
  // A queued bundle here means iOS killed the app between next() and
  // restart(); capgo would install it with an in-process reload on the next
  // backgrounding. Point the slot back at the running bundle (installNext
  // skips it). That marks the running bundle pending, so it must be followed
  // by notifyAppReady() — otherwise capgo rolls back and deletes it.
  if (next && next.id !== current.bundle.id) {
    log('ota', 'clearing stale next bundle', { next: next.version, current: current.bundle.version })
    await CapacitorUpdater.next({ id: current.bundle.id })
      .catch(err => log('ota', 'clearing stale next failed', { error: String(err) }))
  }
  log('ota', 'notifyAppReady')
  await CapacitorUpdater.notifyAppReady()
  noteRollback(current.bundle)
}

// A background swap that capgo later rolls back happens with nobody watching;
// the only trace is booting on an older bundle than last time. A preview on
// either side moves versions in any direction, and a store update boots the
// builtin bundle (plain semver, below any OTA build of it), so neither counts.
function noteRollback(bundle: BundleInfo): void {
  const version = bundle.version
  const preview = getPreviewChannel() !== null
  try {
    const raw = localStorage.getItem(LAST_BOOT_KEY)
    const last = raw ? (JSON.parse(raw) as { version?: string; preview?: boolean }) : null
    if (last?.version && !last.preview && !preview && bundle.id !== 'builtin' && isNewer(last.version, version)) {
      log('ota', 'booted an older bundle than last time', { previous: last.version, current: version })
      Sentry.captureMessage(`OTA bundle ${last.version} was rolled back to ${version}.`, { level: 'warning', fingerprint: ['ota-rolled-back'] })
    }
  } catch {
    // A malformed entry is overwritten below.
  }
  localStorage.setItem(LAST_BOOT_KEY, JSON.stringify({ version, preview }))
}
